import { FileText, LoaderCircle, Printer, X } from "lucide-react";
import { useState } from "react";
import {
  estimatePdfPages,
  exportTiledPdf,
  type PdfExportOptions,
} from "@/src/core/export/pdfExporter";
import { useT } from "@/src/i18n";
import { useMapStore } from "@/src/store/useMapStore";

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  onMessage: (message: string) => void;
}

const selectClass = "mt-1.5 h-10 w-full rounded-lg border border-white/10 bg-[#24221e] px-3 text-xs text-stone-200 outline-none focus:border-[#b4362f]";

export function ExportModal({ open, onClose, onMessage }: ExportModalProps) {
  const getDocument = useMapStore((state) => state.getDocument);
  const mapMode = useMapStore((state) => state.meta.gridType);
  const printGridSize = useMapStore((state) => state.meta.printGridSizeMm);
  const setPrintGridSize = useMapStore((state) => state.setPrintGridSizeMm);
  const [options, setOptions] = useState<PdfExportOptions>({
    orientation: "portrait",
    dpi: 300,
    marginMm: 8,
    overlapMm: 5,
  });
  const t = useT();
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const pages = estimatePdfPages(getDocument(), options);
  if (!open) return null;

  const runExport = async () => {
    try {
      setProgress({ current: 0, total: pages.total });
      await exportTiledPdf(getDocument(), options, (current, total) => setProgress({ current, total }));
      onMessage(t("toast.pdfExported"));
      onClose();
    } catch (error) {
      onMessage(error instanceof Error ? error.message : t("toast.pdfFailed"));
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/60 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="export-title">
      <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-white/10 bg-[#1b1a17] shadow-2xl shadow-black/50">
        <div className="flex items-start justify-between border-b border-white/8 px-5 py-4">
          <div className="flex gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#a8322c]/20 text-[#dd665a]"><Printer size={19} /></div>
            <div><h2 id="export-title" className="font-serif text-lg font-semibold text-stone-100">{t("export.title")}</h2><p className="mt-0.5 text-xs text-stone-500">{t("export.subtitle")}</p></div>
          </div>
          <button type="button" onClick={onClose} disabled={Boolean(progress)} className="rounded-lg p-2 text-stone-500 hover:bg-white/5 hover:text-white" aria-label={t("export.close")}><X size={17} /></button>
        </div>

        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <label className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{t("export.orientation")}
            <select className={selectClass} value={options.orientation} onChange={(event) => setOptions((value) => ({ ...value, orientation: event.target.value as PdfExportOptions["orientation"] }))}>
              <option value="portrait">{t("export.portrait")}</option><option value="landscape">{t("export.landscape")}</option>
            </select>
          </label>
          <label className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{t("export.quality")}
            <select className={selectClass} value={options.dpi} onChange={(event) => setOptions((value) => ({ ...value, dpi: Number(event.target.value) as 150 | 300 }))}>
              <option value={300}>{t("export.dpi300")}</option><option value={150}>{t("export.dpi150")}</option>
            </select>
          </label>
          <label className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{mapMode === "hex" ? t("export.printedHexWidth") : t("export.printedGridCell")}
            <select className={selectClass} value={printGridSize} onChange={(event) => setPrintGridSize(Number(event.target.value))}>
              <option value={25.4}>{t("export.mmInch")}</option><option value={12.7}>{t("export.mmHalfInch")}</option><option value={10}>{t("export.mmCompact")}</option>
            </select>
          </label>
          <label className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{t("export.pageOverlap")}
            <select className={selectClass} value={options.overlapMm} onChange={(event) => setOptions((value) => ({ ...value, overlapMm: Number(event.target.value) }))}>
              <option value={0}>{t("export.overlapNone")}</option><option value={5}>{t("export.overlap5")}</option><option value={10}>{t("export.overlap10")}</option>
            </select>
          </label>
        </div>

        <div className="mx-5 mb-5 flex items-center justify-between rounded-2xl border border-white/8 bg-white/[0.025] p-4">
          <div className="flex items-center gap-3"><FileText size={20} className="text-[#c94d44]" /><div><p className="text-sm font-semibold text-stone-200">{t("export.pages", { count: pages.total })}</p><p className="text-[10px] text-stone-500">{t("export.pageGrid", { columns: pages.columns, rows: pages.rows })}</p></div></div>
          <button
            type="button"
            onClick={() => void runExport()}
            disabled={Boolean(progress) || pages.total > 48}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#ad342e] px-4 text-xs font-semibold text-white shadow-lg shadow-red-950/25 transition hover:bg-[#bf3d35] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {progress ? <LoaderCircle size={15} className="animate-spin" /> : <Printer size={15} />}
            {progress ? `${progress.current}/${progress.total}` : t("export.exportPdf")}
          </button>
        </div>
      </div>
    </div>
  );
}
